/**
 * LayerList — Ordered list of layers with drag-reorder (handle), visibility toggle, and delete.
 */

import { arrayMove } from '@dnd-kit/sortable';
import { Eye, EyeOff, Trash2, Plus } from 'lucide-react';
import { DragHandle, SortableItem, SortableList } from '../../../components/SortableList';
import { CustomLayerDefinition } from '../types';
import { getLayerSchema } from '../layerSchemas';

interface LayerListProps {
  layers: CustomLayerDefinition[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onReorder: (layers: CustomLayerDefinition[]) => void;
  onToggleVisibility: (id: string) => void;
  onDelete: (id: string) => void;
  onAddClick: () => void;
}

export default function LayerList({
  layers, selectedId, onSelect, onReorder, onToggleVisibility, onDelete, onAddClick,
}: LayerListProps) {
  const handleMove = (activeId: string, overId: string) => {
    const from = layers.findIndex(layer => layer.id === activeId);
    const to = layers.findIndex(layer => layer.id === overId);
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(layers, from, to));
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-gray-400 uppercase tracking-wider">Layers</span>
        <button
          onClick={onAddClick}
          className="flex items-center gap-1 text-[10px] text-purple-400 hover:text-purple-300"
        >
          <Plus size={12} /> Add
        </button>
      </div>

      {layers.length === 0 && (
        <div className="text-center py-4 space-y-2">
          <p className="text-xs text-gray-500">No layers yet</p>
          <button
            onClick={onAddClick}
            className="px-3 py-1.5 rounded-lg text-xs bg-purple-600/20 text-purple-300 hover:bg-purple-600/30 transition-colors font-medium"
          >
            + Add your first layer
          </button>
          <p className="text-[10px] text-gray-600 leading-relaxed px-2">
            Start with a background, then add effects on top
          </p>
        </div>
      )}

      <SortableList ids={layers.map(layer => layer.id)} onMove={handleMove}>
      {layers.map(layer => {
        const schema = getLayerSchema(layer.type);
        const isSelected = layer.id === selectedId;

        return (
          <SortableItem key={layer.id} id={layer.id}>{sortable => (
          <div
            ref={sortable.setNodeRef}
            style={sortable.style}
            onClick={() => onSelect(layer.id)}
            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg cursor-pointer transition-all text-xs ${
              isSelected
                ? 'bg-purple-600/30 ring-1 ring-purple-500 text-white'
                : 'bg-gray-800/40 text-gray-300 hover:bg-gray-800'
            } ${sortable.isDragging ? 'ring-1 ring-purple-400 shadow-lg' : ''}`}
          >
            <DragHandle
              bindings={sortable.handleProps}
              label={`Drag ${layer.label || schema?.label || layer.type}`}
              className="p-0.5 text-gray-500"
              iconSize={12}
            />

            <span className="flex-1 truncate">
              {layer.label || schema?.label || layer.type}
            </span>

            <button
              onClick={e => { e.stopPropagation(); onToggleVisibility(layer.id); }}
              className="text-gray-500 hover:text-gray-300 p-0.5"
              title={layer.visible ? 'Hide layer' : 'Show layer'}
            >
              {layer.visible ? <Eye size={12} /> : <EyeOff size={12} />}
            </button>

            <button
              onClick={e => { e.stopPropagation(); onDelete(layer.id); }}
              className="text-gray-500 hover:text-red-400 p-0.5"
              title="Delete layer"
            >
              <Trash2 size={12} />
            </button>
          </div>
          )}</SortableItem>
        );
      })}
      </SortableList>
    </div>
  );
}
